'use client';

import { useCallback, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { RATING_REVIEWS_MENU_ID } from '@headless-workspace/config';
import { useUIContext } from '@headless-workspace/core-ui';
import { RatingAndReviewsFormValue, UserInfo } from '@headless-workspace/domain/common/server';
import {
    GlowButton,
    GlowClickable,
    GlowDivider,
    GlowIcon,
    GlowImage,
    GlowTextBody,
    GlowTitle,
    Icons,
    PropsWithLocale,
} from '@headless-workspace/glow-ds';
import { Component } from '@headless-workspace/typings';
import { RatingsAndReviewsForm } from './RatingsAndReviewsForm';

type RatingsAndReviewsModalProps = PropsWithLocale & {
    productId: string;
    userInfo: Readonly<UserInfo>;
    ratingAndReviewsForm: RatingAndReviewsFormValue;
};

const IMG_WIDTH = 96;
const IMG_HEIGHT = 96;

export const RatingsAndReviewsModal: Component<RatingsAndReviewsModalProps> = ({
    productId,
    userInfo,
    locale,
    ratingAndReviewsForm,
}) => {
    const [isSubmitted, setIsSubmitted] = useState(false);
    const t = useTranslations('Discover.Pdp.ratingAndReviewsForm');
    const { menuModal } = useUIContext();

    const closeRatingsAndReviewsModal = useCallback(() => menuModal.close(RATING_REVIEWS_MENU_ID), [menuModal]);

    useEffect(() => {
        setIsSubmitted(false);
    }, [productId]);

    const handleSubmitForm = useCallback((isSubmit: boolean) => {
        setIsSubmitted(isSubmit);
    }, []);

    const handleReviewConfirmation = () => {
        // FIXME: Implement review confirmation logic if needed
    };

    const { product, fields } = ratingAndReviewsForm;

    return (
        <div className={'h-fullViewport w-fullViewport tablet:w-sideMenuTablet flex flex-col bg-background-l3'}>
            <ModalHeader title={t('label.modalTitle')} onClose={closeRatingsAndReviewsModal} />

            <div className={'flex flex-1 flex-col p-1 max-h-ratingReviewFormModalContainer overflow-x-auto'}>
                <ProductInfo name={product.name} brand={product.brand.name} imageUrl={product.imageUrl} />

                {!isSubmitted ? (
                    <RatingsAndReviewsForm
                        formInfo={fields}
                        onSubmit={handleSubmitForm}
                        locale={locale}
                        userInfo={userInfo}
                    />
                ) : (
                    <ReviewInformation label={t('action.sendReview')} onConfirm={handleReviewConfirmation} />
                )}
            </div>
        </div>
    );
};

const ModalHeader = ({ title, onClose }: { title: string; onClose: () => void }) => (
    <div
        className={
            'flex flex-row justify-between items-center p-1 gap-1 border-b-medium border-b-border-subtle border-solid h-headerFixedNavDesktop'
        }
    >
        <GlowTitle text={title} fontSize={'title4'} className={'flex-1 text-left'} />
        <GlowClickable
            onClick={onClose}
            content={{
                icon: <GlowIcon Icon={Icons.Cross} type={'large'} className={'text-text-primary'} />,
            }}
        />
    </div>
);

const ProductInfo = ({ name, brand, imageUrl }: { name: string; brand: string; imageUrl: string }) => (
    <div className={'flex flex-row gap-1 items-center pb-1'}>
        <GlowImage
            className={'p-0.125 border-medium border-solid rounded-0.5 border-primary bg-background-l3'}
            image={{ src: imageUrl, alt: name }}
            width={IMG_WIDTH}
            height={IMG_HEIGHT}
        />
        <div className={'flex flex-col'}>
            <GlowTextBody text={name} />
            <GlowTextBody text={brand} fontWeight={'bold'} />
        </div>
    </div>
);

const ReviewInformation = ({ onConfirm, label }: { onConfirm: () => void; label: string }) => (
    <>
        <div className={'flex flex-1 flex-col'}>TODO : Display Recap</div>

        <div className={'absolute w-full bg-background-l0 bottom-0 -ml-1'}>
            <GlowDivider />
            <div className={'flex items-center justify-center p-1 flex-col gap-0.5'}>
                <GlowButton
                    className={'flex-1 w-full'}
                    label={label}
                    variant={'primary'}
                    onClick={onConfirm}
                    isSpinnerActive={false}
                />
            </div>
        </div>
    </>
);
